import * as constants from "./constants";

// Welche Aufgabentypen beim Aufschlagen gleich ihr Fenster oeffnen duerfen.
// "todo" hat keins, und bei "email" muss der Nutzer erst im Aktivitaetsverlauf
// nachsehen, worum es ueberhaupt geht — bei einem Anruf weiss er das schon.
const AUTO_OPEN_EXCLUDED = ["todo", "email"];
const autoModal = (task) =>
  task?.taskType && !AUTO_OPEN_EXCLUDED.includes(task.taskType) ? task.taskType : null;

const INIT_STATE = {
  isActive: false,
  queue: [],
  currentIndex: 0,
  activeModal: null, // "call" | "email" | "meeting" | "edit" | null
  handled: {}, // taskId -> "completed" | "skipped" | "rescheduled" | "deleted"
  edits: {}, // taskId -> updated task object
};

// Ein Neuladen der Seite mitten in der Sitzung darf die Warteschlange nicht
// wegwerfen: welche Aufgaben dran sind, welche schon abgehakt wurden und welche
// Notizen daran haengen, steht NUR hier — es ist zu diesem Zeitpunkt nirgends
// gespeichert. Deshalb geht der Zustand durch den sessionStorage.
//
// sessionStorage und nicht localStorage: eine Arbeitssitzung gehoert zu diesem Tab
// und zu diesem Arbeitsgang. Eine von gestern beim naechsten Anmelden wieder
// aufpoppen zu lassen waere schlimmer, als sie zu verlieren.
//
// ponytail: ein Schluessel mit JSON, kein redux-persist. Traegt, solange der
// Zustand serialisierbar bleibt (Aufgaben aus GraphQL sind es); kaeme etwas
// hinein, das es nicht ist, waere eine Auswahl der zu speichernden Felder faellig.
const STORAGE_KEY = "workSession";

const loadPersisted = () => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    const saved = raw ? JSON.parse(raw) : null;
    // Nur eine laufende Sitzung mit Aufgaben ist eine Sitzung.
    if (!saved?.isActive || !Array.isArray(saved.queue) || !saved.queue.length) {
      return null;
    }
    return { ...INIT_STATE, ...saved, activeModal: null };
  } catch (error) {
    // Kein Zugriff (privates Fenster, gesperrter Speicher) oder kaputter Inhalt:
    // dann eben ohne. Eine verlorene Sitzung ist kein Datenverlust.
    return null;
  }
};

/**
 * Wird nach jeder Aenderung am Zustand aus dem Store gerufen (redux/store.js).
 * Der Reducer bleibt damit rein — geschrieben wird ausserhalb.
 */
export const persistWorkSession = (state) => {
  try {
    if (state?.isActive) {
      window.sessionStorage.setItem(
        STORAGE_KEY,
        // activeModal NICHT: ein Refresh soll kein Fenster wieder aufreissen.
        JSON.stringify({ ...state, activeModal: null })
      );
    } else {
      window.sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch (error) {
    // Speichern ist eine Zugabe: ohne sie ist die Sitzung nach einem Refresh weg,
    // aber alles andere laeuft weiter.
  }
};

const WorkSession = (state = loadPersisted() || INIT_STATE, action) => {
  switch (action.type) {
    case constants.START_WORK_SESSION: {
      const { tasks, initialIndex = 0 } = action.payload || {};
      const safeTasks = Array.isArray(tasks) ? tasks : [];
      const firstTask = safeTasks[initialIndex] || safeTasks[0];
      return {
        ...state,
        isActive: safeTasks.length > 0,
        queue: safeTasks,
        currentIndex: initialIndex,
        handled: {},
        edits: {},
        activeModal: autoModal(firstTask),
      };
    }
    case constants.END_WORK_SESSION:
      return {
        ...INIT_STATE,
      };
    case constants.SET_WORK_SESSION_INDEX: {
      const nextIdx = Math.max(0, Math.min(action.payload, state.queue.length - 1));
      const nextTask = state.queue[nextIdx];
      return {
        ...state,
        currentIndex: nextIdx,
        activeModal: autoModal(nextTask),
      };
    }
    case constants.NEXT_WORK_SESSION_TASK: {
      const nextIdx = state.currentIndex + 1;
      if (nextIdx >= state.queue.length) {
        return {
          ...state,
          isActive: false,
          activeModal: null,
        };
      }
      const nextTask = state.queue[nextIdx];
      return {
        ...state,
        currentIndex: nextIdx,
        activeModal: autoModal(nextTask),
      };
    }
    case constants.PREV_WORK_SESSION_TASK: {
      const prevIdx = Math.max(0, state.currentIndex - 1);
      const prevTask = state.queue[prevIdx];
      return {
        ...state,
        currentIndex: prevIdx,
        activeModal: autoModal(prevTask),
      };
    }
    case constants.SET_WORK_SESSION_TASK_HANDLED: {
      const { taskId, status } = action.payload;
      return {
        ...state,
        handled: {
          ...state.handled,
          [taskId]: status,
        },
      };
    }
    case constants.UPDATE_WORK_SESSION_TASK: {
      const { taskId, taskData } = action.payload;
      return {
        ...state,
        edits: {
          ...state.edits,
          [taskId]: {
            ...(state.edits[taskId] || state.queue.find((t) => t.id === taskId) || {}),
            ...taskData,
          },
        },
      };
    }
    case constants.OPEN_WORK_SESSION_MODAL:
      return {
        ...state,
        activeModal: action.payload,
      };
    case constants.CLOSE_WORK_SESSION_MODAL:
      return {
        ...state,
        activeModal: null,
      };
    default:
      return state;
  }
};

export default WorkSession;
